import { FC, useMemo } from 'react';
import { Avatar, Card, Flex, Statistic, Typography } from 'antd';
import {
    TbCalendarDollar,
    TbCalendarEvent,
    TbCalendarExclamation,
    TbCalendarStats,
    TbChartHistogram,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ButtonSkeleton } from '@/shared/components/skeletons';

import type { MarketCalendarEvent } from '../calendarEvents';
import { summarizeCalendarPeriod } from './summary';

interface CalendarSummaryCardsProps {
    events: readonly MarketCalendarEvent[];
    from: string;
    loading: boolean;
    periodLabel: string;
    to: string;
}

const CalendarSummaryCards: FC<CalendarSummaryCardsProps> = ({
    events,
    from,
    loading,
    periodLabel,
    to,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const summary = useMemo(() => summarizeCalendarPeriod(events, from, to), [events, from, to]);
    const cards = [
        {
            description: `${periodLabel} across active filters`,
            Icon: TbCalendarStats,
            key: 'total',
            label: 'Events in View',
            value: summary.totalEventCount,
        },
        {
            description: `Scheduled during ${periodLabel}`,
            Icon: TbCalendarDollar,
            key: 'dividends',
            label: 'Dividend Events',
            value: summary.dividendCount,
        },
        {
            description: `Economic releases during ${periodLabel}`,
            Icon: TbChartHistogram,
            key: 'economic',
            label: 'Economic Releases',
            value: summary.economicEventCount,
        },
        {
            description: `High-impact releases during ${periodLabel}`,
            Icon: TbCalendarExclamation,
            key: 'high-impact',
            label: 'High Impact',
            value: summary.highImpactEventCount,
        },
        {
            description: summary.busiestDate
                ? new Date(`${summary.busiestDate}T12:00:00`).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      weekday: 'long',
                  })
                : `No events during ${periodLabel}`,
            Icon: TbCalendarEvent,
            key: 'busiest-day',
            label: 'Busiest Day',
            value: summary.busiestEventCount,
        },
    ];

    return (
        <div
            aria-label='Events overview'
            style={{
                display: 'grid',
                gap: '0.5rem',
                gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
                width: '100%',
            }}
        >
            {cards.map(({ description, Icon, key, label, value }) => (
                <Card
                    key={key}
                    size='small'
                    styles={{ body: { padding: '0.75rem' } }}
                    style={{ borderColor: token.colorBorder, boxShadow: 'none', minWidth: 0 }}
                >
                    <Flex align='center' gap={6} style={{ marginBottom: '0.5rem', minWidth: 0 }}>
                        <Avatar
                            icon={<Icon size='0.875rem' />}
                            shape='square'
                            size='small'
                            style={{
                                backgroundColor: token.colorFillQuaternary,
                                border: `1px solid ${token.colorBorder}`,
                                color: token.colorTextSecondary,
                                flex: 'none',
                            }}
                        />
                        <Typography.Text ellipsis type='secondary'>
                            {label}
                        </Typography.Text>
                    </Flex>
                    {loading ? (
                        <ButtonSkeleton active size='small' style={{ height: '1.5rem', width: '4rem' }} />
                    ) : (
                        <Statistic
                            value={value.toLocaleString()}
                            valueStyle={{ fontSize: token.fontSizeHeading3, lineHeight: 1.2 }}
                        />
                    )}
                    <Typography.Text
                        ellipsis
                        style={{ color: token.colorTextTertiary, display: 'block', marginTop: '0.25rem' }}
                    >
                        <small>{description}</small>
                    </Typography.Text>
                </Card>
            ))}
        </div>
    );
};

export default CalendarSummaryCards;
